import apiClient, { ApiClient } from './ApiClient';
import { PushRepository } from './PushRepository';

const OWNER_KEY = 'claw-push-owner';
const accountKey = (account) => `claw-push-${account}`;
const read = (key) => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const write = (key, value) => {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch { /* In-memory opt-in still works when storage is unavailable. */ }
};

export const base64UrlBytes = (value) => {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/')
    + '='.repeat((4 - value.length % 4) % 4);
  return Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
};

export const pushSupportMessage = () => {
  if (!window.isSecureContext) return 'Device notifications require a secure HTTPS connection.';
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (ios && !window.matchMedia('(display-mode: standalone)').matches && !navigator.standalone) {
    return 'Install Claw on your Home Screen and open it there to enable device notifications.';
  }
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return 'Device notifications are not supported in this browser.';
  }
  return '';
};

const withTimeout = (promise, milliseconds, message) => new Promise((resolve, reject) => {
  const timer = setTimeout(() => reject(new Error(message)), milliseconds);
  Promise.resolve(promise).then(resolve, reject).finally(() => clearTimeout(timer));
});

export class PushNotificationService {
  constructor() {
    this.session = null;
    this.version = 0;
    this.queue = Promise.resolve();
    this.cleanup = Promise.resolve();
    this.listeners = new Set();
    this.owner = read(OWNER_KEY);
    this.snapshot = { enabled: false, busy: false, available: false, message: '' };
  }

  subscribe = (listener) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
  getSnapshot = () => this.snapshot;
  publish(changes) {
    this.snapshot = { ...this.snapshot, ...changes };
    this.listeners.forEach((listener) => listener());
  }
  enqueue(operation) {
    const result = this.queue.then(operation);
    this.queue = result.catch(() => {});
    return result;
  }
  assertCurrent(session) {
    if (this.session !== session || session.version !== this.version) {
      throw new Error('Your account changed. Please try again after signing in.');
    }
  }
  repository(token = read('accessToken')) {
    // Isolate device requests from global auth changes and recursive token-refresh/logout.
    // Read the latest session token for each attempt; never persist it in the worker.
    const client = new ApiClient(apiClient.baseUrl);
    client.setToken(token);
    const request = client.request.bind(client);
    client.request = (...args) => withTimeout(request(...args), 10000, 'Unable to reach device notifications. Please retry.');
    return new PushRepository(client);
  }
  async registration(wait = false) {
    const registration = await navigator.serviceWorker.getRegistration('/');
    if (registration?.active && registration.pushManager) return registration;
    if (!wait) return null;
    return withTimeout(navigator.serviceWorker.ready, 8000,
      'The Claw app is not ready for notifications. Refresh the published app and try again.');
  }
  async removeBrowserSubscription(session = null) {
    if (!('serviceWorker' in navigator)) return;
    const registration = await this.registration();
    if (session) this.assertCurrent(session);
    const subscription = await registration?.pushManager?.getSubscription();
    if (session) this.assertCurrent(session);
    if (subscription && !(await subscription.unsubscribe())) {
      throw new Error('Unable to turn off device notifications. Please retry.');
    }
  }

  setAccount(user) {
    const id = user?.id || user?.userId;
    const companyId = user?.company?.id;
    if (!id || !companyId || user.isEnabled === false) return this.endSession();
    const account = `${companyId}:${id}`;
    if (this.session?.account === account) return this.queue;
    const cleanup = this.session ? this.endSession(this.session.token) : this.cleanup;
    const session = { account, version: ++this.version, token: read('accessToken'),
      id: read(accountKey(account)), optedIn: read(`${accountKey(account)}:enabled`) === 'true' };
    this.session = session;
    this.publish({ enabled: false, busy: true, available: false, message: '' });
    return this.enqueue(async () => {
      try {
        await cleanup;
        this.assertCurrent(session);
        const support = pushSupportMessage();
        if (support) { this.publish({ message: support }); return; }
        const owner = this.owner || read(OWNER_KEY);
        // Never adopt another user's (or an unidentifiable) browser subscription.
        if (owner !== account) {
          await this.removeBrowserSubscription(session);
          this.assertCurrent(session);
          if (owner) write(accountKey(owner), null);
          write(OWNER_KEY, null);
        }
        const registration = await this.registration();
        const existing = await registration?.pushManager?.getSubscription();
        this.assertCurrent(session);
        this.publish({ enabled: Boolean(session.optedIn && existing && Notification.permission === 'granted') });
        const config = await this.repository().getConfig();
        this.assertCurrent(session);
        this.publish({ available: Boolean(config.enabled && config.publicKey), message: config.enabled
          ? '' : 'Device notifications are currently unavailable.' });
        if (session.optedIn && Notification.permission === 'granted' && config.enabled) {
          await this.register(session, config);
        } else if (session.optedIn && Notification.permission === 'denied') {
          await this.removeBrowserSubscription(session);
          this.publish({ message: 'Notifications are blocked. Allow them in your browser settings to enable them again.' });
        }
      } catch (error) {
        if (this.session === session) this.publish({ message: this.errorMessage(error) });
      } finally {
        if (this.session === session) this.publish({ busy: false });
      }
    });
  }

  errorMessage(error) {
    if (error?.status === 401) return 'Your session needs refreshing. Refresh Claw and try again.';
    return error?.data?.message || error?.message || 'Unable to update device notifications.';
  }

  async register(session, config) {
    this.assertCurrent(session);
    const registration = await this.registration(true);
    this.assertCurrent(session);
    const key = base64UrlBytes(config.publicKey);
    let subscription = await registration.pushManager.getSubscription();
    this.assertCurrent(session);
    let replaceRecord = !subscription && Boolean(session.id);
    const existingKey = subscription?.options?.applicationServerKey;
    if (subscription && (!existingKey || Array.from(new Uint8Array(existingKey)).join(',') !== Array.from(key).join(','))) {
      if (!(await subscription.unsubscribe())) throw new Error('Unable to refresh device notifications. Please retry.');
      subscription = null;
      replaceRecord = Boolean(session.id);
    }
    this.assertCurrent(session);
    if (!subscription) subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
    // A logout may occur while the browser's subscribe dialog is resolving.
    if (this.session !== session || session.version !== this.version) {
      await subscription.unsubscribe();
      this.assertCurrent(session);
    }
    const token = read('accessToken');
    session.token = token;
    const repository = this.repository(token);
    const save = async () => {
      if (replaceRecord && session.id) {
        try { return await repository.update(session.id, subscription.toJSON()); }
        catch (error) { if (error.status !== 404) throw error; }
      }
      return repository.register(subscription.toJSON());
    };
    let saved;
    try {
      saved = await save();
    } catch (error) {
      // A concurrent upsert may return 400. Refresh configuration and retry once;
      // other validation/ownership failures are surfaced, never silently transferred.
      if (error.status !== 400) throw error;
      this.assertCurrent(session);
      const refreshed = await repository.getConfig();
      if (!refreshed.enabled || refreshed.publicKey !== config.publicKey) throw error;
      saved = await save();
    }
    if (this.session !== session || session.version !== this.version) {
      await subscription.unsubscribe();
      if (saved?.id) await repository.delete(saved.id).catch(() => {});
      this.assertCurrent(session);
    }
    if (!saved?.id) throw new Error('Device notifications could not be registered. Please retry.');
    write(accountKey(session.account), saved.id);
    session.id = saved.id;
    this.owner = session.account;
    write(OWNER_KEY, session.account);
    write(`${accountKey(session.account)}:enabled`, 'true');
    session.optedIn = true;
    this.publish({ enabled: true, message: '' });
  }

  enable() {
    const session = this.session;
    const support = pushSupportMessage();
    if (!session || support || !this.snapshot.available || this.snapshot.busy) return Promise.resolve();
    // Invoke synchronously in the click handler, before network calls lose user activation.
    let permission;
    try {
      permission = Notification.permission === 'granted'
        ? Promise.resolve('granted') : Notification.requestPermission();
    } catch (error) {
      this.publish({ message: this.errorMessage(error) });
      return Promise.resolve();
    }
    this.publish({ busy: true, message: '' });
    return this.enqueue(async () => {
      try {
        if (await permission !== 'granted') {
          throw new Error('Notifications were not allowed. You can enable them in your browser settings.');
        }
        this.assertCurrent(session);
        const config = await this.repository().getConfig();
        if (!config.enabled || !config.publicKey) throw new Error('Device notifications are currently unavailable.');
        await this.register(session, config);
      } catch (error) {
        if (this.session === session) this.publish({ message: this.errorMessage(error) });
      } finally {
        if (this.session === session) this.publish({ busy: false });
      }
    });
  }

  disable() {
    const session = this.session;
    if (!session || this.snapshot.busy) return Promise.resolve();
    this.publish({ busy: true, message: '' });
    return this.enqueue(async () => {
      try {
        this.assertCurrent(session);
        session.optedIn = false;
        write(`${accountKey(session.account)}:enabled`, null);
        const id = session.id || read(accountKey(session.account));
        try {
          if (id) await this.repository().delete(id);
        } finally {
          await this.removeBrowserSubscription(session);
          this.assertCurrent(session);
          write(accountKey(session.account), null);
          write(OWNER_KEY, null);
          session.id = null;
          this.owner = null;
          this.publish({ enabled: false });
        }
      } catch (error) {
        if (this.session === session) this.publish({ message: this.errorMessage(error) });
      } finally {
        if (this.session === session) this.publish({ busy: false });
      }
    });
  }

  reconcile() {
    const session = this.session;
    if (!session?.optedIn || this.snapshot.busy || pushSupportMessage()) return Promise.resolve();
    this.publish({ busy: true });
    return this.enqueue(async () => {
      try {
        this.assertCurrent(session);
        if (Notification.permission !== 'granted') {
          await this.removeBrowserSubscription(session);
          this.publish({ enabled: false, message: 'Allow notifications in your browser settings to enable them again.' });
          return;
        }
        const config = await this.repository().getConfig();
        this.assertCurrent(session);
        if (config.enabled && config.publicKey) await this.register(session, config);
      } catch (error) {
        if (this.session === session) this.publish({ message: this.errorMessage(error) });
      } finally {
        if (this.session === session) this.publish({ busy: false });
      }
    });
  }

  endSession(token = read('accessToken')) {
    const owner = this.session?.account || this.owner || read(OWNER_KEY);
    const id = this.session?.id || (owner && read(accountKey(owner)));
    this.version += 1;
    this.session = null;
    this.queue = Promise.resolve();
    this.publish({ enabled: false, busy: false, available: false, message: '' });
    // Invalidate pending registration immediately. Cleanup must not wait on a
    // permission dialog or a request that initiated authentication failure.
    const previousCleanup = this.cleanup;
    this.cleanup = (async () => {
      await previousCleanup;
      await this.removeBrowserSubscription().catch(() => {});
      if (owner) write(accountKey(owner), null);
      write(OWNER_KEY, null);
      this.owner = null;
      if (id && token) {
        await withTimeout(this.repository(token).delete(id), 5000, 'Push cleanup timed out.').catch(() => {});
      }
    })();
    // Preserve the account's opt-in preference for its next authenticated login.
    return this.cleanup;
  }
}

export default new PushNotificationService();
